import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Bike, ChevronRight, Tag } from 'lucide-react';

const CATEGORIA_COLORS = {
  'naked': '#EF4444', 'scooter': '#3B82F6', 'enduro': '#10B981', 'on-off': '#F59E0B',
  'sport tourer': '#8B5CF6', 'custom cruiser': '#EC4899', 'hypersport': '#F97316',
  'motocross': '#14B8A6', 'motard': '#6366F1', 'trial': '#84CC16', 'trike': '#A855F7',
  'flat track': '#D946EF', 'minicross': '#06B6D4', 'minimotard': '#0EA5E9',
  'pit bike': '#64748B', 'quadriciclo': '#78716C',
};

export const RicercaVeicoli = () => {
  const [marche, setMarche] = useState([]);
  const [modelli, setModelli] = useState([]);
  const [allestimenti, setAllestimenti] = useState([]);
  const [selectedMarca, setSelectedMarca] = useState('');
  const [selectedModello, setSelectedModello] = useState('');
  const [selectedAllestimento, setSelectedAllestimento] = useState(null);
  const [marcaSearch, setMarcaSearch] = useState('');
  const [modelloSearch, setModelloSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/veicoli/marche').then(r => setMarche(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedMarca) {
      setModelli([]); setAllestimenti([]); setSelectedModello(''); setSelectedAllestimento(null);
      setLoading(true);
      api.get('/veicoli/modelli', { params: { marca: selectedMarca } })
        .then(r => setModelli(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [selectedMarca]);

  useEffect(() => {
    if (selectedMarca && selectedModello) {
      setAllestimenti([]); setSelectedAllestimento(null);
      api.get('/veicoli/allestimenti', { params: { marca: selectedMarca, modello: selectedModello } })
        .then(r => setAllestimenti(r.data)).catch(() => {});
    }
  }, [selectedMarca, selectedModello]);

  const filteredMarche = marcaSearch ? marche.filter(m => m.toLowerCase().includes(marcaSearch.toLowerCase())) : marche;
  const filteredModelli = modelloSearch ? modelli.filter(m => m.toLowerCase().includes(modelloSearch.toLowerCase())) : modelli;

  return (
    <div data-testid="ricerca-veicoli">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Marca */}
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">1. Marca</Label>
          {selectedMarca ? (
            <div className="border-2 border-blue-400 rounded-lg p-3 bg-blue-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-blue-700">{selectedMarca}</span>
                <button onClick={() => { setSelectedMarca(''); setSelectedModello(''); setSelectedAllestimento(null); setAllestimenti([]); setModelli([]); }}
                  className="text-xs text-blue-500 hover:text-blue-700 font-medium px-2 py-0.5 rounded hover:bg-blue-100" data-testid="reset-marca">Cambia</button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={marcaSearch} onChange={e => setMarcaSearch(e.target.value)} placeholder="Cerca marca..." className="pl-8 h-8 text-sm" data-testid="marca-search" />
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                {filteredMarche.map(m => (
                  <button key={m} onClick={() => { setSelectedMarca(m); setMarcaSearch(''); }}
                    className="w-full text-left px-3 py-2 text-sm border-b border-slate-50 hover:bg-slate-50 text-slate-700 transition-colors"
                    data-testid={`marca-${m}`}>
                    {m}
                  </button>
                ))}
                {filteredMarche.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Nessuna marca</p>}
              </div>
            </>
          )}
        </div>

        {/* Modello */}
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">2. Modello</Label>
          {selectedModello ? (
            <div className="border-2 border-blue-400 rounded-lg p-3 bg-blue-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-blue-700">{selectedModello}</span>
                <button onClick={() => { setSelectedModello(''); setSelectedAllestimento(null); setAllestimenti([]); }}
                  className="text-xs text-blue-500 hover:text-blue-700 font-medium px-2 py-0.5 rounded hover:bg-blue-100" data-testid="reset-modello">Cambia</button>
              </div>
            </div>
          ) : selectedMarca ? (
            <>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={modelloSearch} onChange={e => setModelloSearch(e.target.value)} placeholder="Cerca modello..." className="pl-8 h-8 text-sm" data-testid="modello-search" />
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                {loading ? <p className="text-xs text-slate-400 text-center py-4">Caricamento...</p> :
                  filteredModelli.map(m => (
                    <button key={m} onClick={() => { setSelectedModello(m); setModelloSearch(''); }}
                      className="w-full text-left px-3 py-2 text-sm border-b border-slate-50 hover:bg-slate-50 text-slate-700 transition-colors"
                      data-testid={`modello-${m}`}>
                      {m}
                    </button>
                  ))}
                {!loading && filteredModelli.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Nessun modello</p>}
              </div>
            </>
          ) : (
            <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center">
              <p className="text-xs text-slate-400">Seleziona prima una marca</p>
            </div>
          )}
        </div>

        {/* Allestimento + Risultato */}
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">3. Allestimento</Label>
          {selectedModello ? (
            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
              {allestimenti.map((a, i) => {
                const cat = a.Categoria || '';
                const catColor = CATEGORIA_COLORS[cat] || '#64748B';
                const isSelected = selectedAllestimento === i;
                return (
                  <button key={i} onClick={() => setSelectedAllestimento(i)}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-50 transition-colors ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                    data-testid={`allestimento-${i}`}>
                    <p className="text-sm text-slate-700 font-medium">{a.Allestimento || '(base)'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: catColor + '15', color: catColor, border: `1px solid ${catColor}30` }}>
                        <Tag size={9} /> {cat || 'N/D'}
                      </span>
                      {a.cm3 && <span className="text-[10px] text-slate-400">{a.cm3} cm³</span>}
                      {a.Kw && <span className="text-[10px] text-slate-400">{a.Kw} Kw</span>}
                    </div>
                  </button>
                );
              })}
              {allestimenti.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Nessun allestimento</p>}
            </div>
          ) : (
            <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center">
              <p className="text-xs text-slate-400">Seleziona prima un modello</p>
            </div>
          )}
        </div>
      </div>

      {/* Result Card */}
      {selectedAllestimento !== null && allestimenti[selectedAllestimento] && (
        <div className="mt-5 p-5 rounded-xl border-2 border-slate-200 bg-white" data-testid="veicolo-result">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <Bike size={24} className="text-slate-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{selectedMarca} {selectedModello}</h3>
              <p className="text-sm text-slate-500">{allestimenti[selectedAllestimento].Allestimento || '(base)'}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {(() => {
              const a = allestimenti[selectedAllestimento];
              const cat = a.Categoria || '';
              const catColor = CATEGORIA_COLORS[cat] || '#64748B';
              return (
                <>
                  <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: catColor + '12', border: `2px solid ${catColor}` }}>
                    <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: catColor }}>Categoria</p>
                    <p className="text-lg font-bold" style={{ color: catColor }}>{cat || 'N/D'}</p>
                  </div>
                  {a.cm3 && (
                    <div className="px-4 py-2 rounded-lg bg-slate-50 border border-slate-200">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Cilindrata</p>
                      <p className="text-lg font-bold text-slate-900">{a.cm3} cm³</p>
                    </div>
                  )}
                  {a.Kw && (
                    <div className="px-4 py-2 rounded-lg bg-slate-50 border border-slate-200">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Potenza</p>
                      <p className="text-lg font-bold text-slate-900">{a.Kw} Kw</p>
                    </div>
                  )}
                  {a.Posti && (
                    <div className="px-4 py-2 rounded-lg bg-slate-50 border border-slate-200">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Posti</p>
                      <p className="text-lg font-bold text-slate-900">{a.Posti}</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default RicercaVeicoli;
